import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { compositeScore, updateMe, scoreHistory } from "@/lib/api";
import ScoreRing from "@/components/wishire/ScoreRing";
import CountUp from "@/components/wishire/CountUp";
import CoachingPlan from "@/components/wishire/CoachingPlan";
import { StarterCoursesNudge } from "@/components/wishire/CourseCards";
import { FileText, Mic, Brain, ArrowRight, Sparkles, CheckCircle2, Share2, Copy, Code, Linkedin, GraduationCap, Megaphone, TrendingUp } from "lucide-react";
import { myStanding, mySeason, myReport } from "@/lib/campusApi";
import { openCandidateReports } from "@/lib/candidateReport";

export default function Dashboard() {
  const { user, refresh } = useAuth();
  const [composite, setComposite] = useState(null);
  const [loading, setLoading] = useState(true);
  const [history, setHistory] = useState([]);
  const [targetRole, setTargetRole] = useState(user?.target_role || "");
  const [savingRole, setSavingRole] = useState(false);
  const [scoreUp, setScoreUp] = useState(0); // points gained since last visit

  const load = async () => {
    try {
      const { data } = await compositeScore();
      setComposite(data);
      // Score-up feedback: compare against the last score this browser saw.
      try {
        const key = "wsh_last_score";
        const prev = Number(localStorage.getItem(key));
        const cur = Number(data?.score || 0);
        if (!Number.isNaN(prev) && prev > 0 && cur > prev) {
          setScoreUp(cur - prev);
          setTimeout(() => setScoreUp(0), 5000);
        }
        // Defer the write past the synchronous StrictMode double-mount window so
        // the live instance still reads the OLD value (write would otherwise poison it).
        if (cur > 0) setTimeout(() => { try { localStorage.setItem(key, String(cur)); } catch (_) {} }, 1500);
      } catch (_) {}
    } catch (e) {
      setComposite({ score: 0, breakdown: {} });
    } finally {
      setLoading(false);
    }
    try {
      const { data: hist } = await scoreHistory();
      setHistory(hist?.history || hist || []);
    } catch (e) {}
  };

  useEffect(() => {
    load();
  }, []);

  const saveTargetRole = async () => {
    setSavingRole(true);
    try {
      await updateMe({ target_role: targetRole });
      await refresh();
      await load();
    } finally {
      setSavingRole(false);
    }
  };

  const score = composite?.score ?? 0;
  const hasResume = composite?.has_resume;
  const hasInterview = composite?.has_interview;
  const hasLinkedIn = !!(user?.linkedin_url);
  const linkedinScore = user?.linkedin_score ?? null;
  const nextMove = computeNextMove(composite, user);
  const tier = scoreTier(score);
  // First visit = no signal yet. Lead with the work, not the zeros.
  const isNew = !loading && !hasResume && !hasInterview && !composite?.has_mock_test && !hasLinkedIn && score === 0;

  return (
    <div className="container-x py-10 md:py-14">
      {/* Score-up toast */}
      {scoreUp > 0 && (
        <div
          data-testid="dash-score-up"
          className="fixed top-6 right-6 z-50 flex items-center gap-3 rounded-2xl bg-emerald-600 text-white px-5 py-3.5 shadow-[0_20px_50px_-20px_rgba(16,128,61,0.7)] animate-[fadeIn_0.3s_ease]"
        >
          <Sparkles size={18} />
          <div>
            <p className="font-display text-lg leading-none">+{scoreUp} points!</p>
            <p className="text-xs text-emerald-100 mt-0.5">Your Wishire Score went up.</p>
          </div>
        </div>
      )}

      {/* Campus-first panel for students linked to a college */}
      {user?.role === "candidate" && user?.college_id && <CampusStudentBanner />}

      {/* Greeting */}
      <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-12">
        <div>
          <span className="inline-flex items-center gap-1.5 text-xs font-mono uppercase tracking-widest text-[#C2410C] bg-[#C2410C]/10 px-3 py-1 rounded-full">
            <Sparkles size={12} /> {isNew ? "Welcome to Wishire" : "Welcome back"}
          </span>
          <h1 className="font-display text-4xl md:text-5xl mt-3 leading-tight">
            Hi {(user?.name || "friend").split(" ")[0]}, let&apos;s get you{" "}
            <span className="vib-text">hired.</span>
          </h1>
        </div>
        <div className="flex items-center gap-2 max-w-md">
          <input
            type="text"
            data-testid="dash-target-role-input"
            placeholder="Role you're aiming for (broad is fine)"
            value={targetRole}
            onChange={(e) => setTargetRole(e.target.value)}
            className="flex-1 px-4 py-2.5 rounded-full bg-white border border-stone-300 placeholder:text-stone-400 focus:outline-none focus:border-stone-900 text-sm"
          />
          <button
            data-testid="dash-target-role-save"
            onClick={saveTargetRole}
            disabled={savingRole || !targetRole.trim()}
            className="btn-primary text-sm disabled:opacity-60"
          >
            Save
          </button>
        </div>
      </div>

      {/* First visit: the work comes first, the score sections wait until there is a score */}
      {isNew && (
        <div className="mb-10" data-testid="dash-first-visit">
          <div className="rounded-3xl border border-stone-200 bg-white p-6 md:p-7 mb-6">
            <span className="overline">How Wishire works</span>
            <div className="mt-3 grid sm:grid-cols-3 gap-4 text-sm text-stone-700">
              <p><b className="text-stone-900">1. Do the starter courses</b> below — every student at your college starts here.</p>
              <p><b className="text-stone-900">2. Follow your plan</b> in Coach — built for the companies coming to your campus.</p>
              <p><b className="text-stone-900">3. Get a score</b> by scoring your résumé and running one mock interview. Drives shortlist from it.</p>
            </div>
          </div>
          <StarterCoursesNudge />
          <div className="mt-6 rounded-3xl border border-stone-200 bg-white p-6 md:p-7">
            <div className="flex items-center justify-between mb-4"><div><span className="overline">My Plan</span><h2 className="font-display text-2xl mt-1">This week</h2></div><Link to="/app/coach?tab=plan" className="text-sm text-[#C2410C] hover:text-stone-900 inline-flex items-center gap-1">Full plan <ArrowRight size={14} /></Link></div>
            <CoachingPlan autoLoad compact />
          </div>
        </div>
      )}

      {/* Your next best move */}
      {!loading && nextMove && (
        <Link
          to={nextMove.to}
          data-testid="dash-next-move"
          className="group vib-pop flex items-center gap-4 mb-8 rounded-3xl p-[2px] bg-gradient-to-r from-[#C2410C] via-[#D97706] to-[#F59E0B] shadow-[0_18px_45px_-24px_rgba(194,65,12,0.6)]"
        >
          <div className="flex items-center gap-4 w-full rounded-[calc(1.5rem-2px)] bg-[#fffdf9] p-5">
            <span className="inline-flex p-3 rounded-2xl bg-gradient-to-br from-[#C2410C] to-[#F59E0B] text-white shrink-0 vib-pulse">
              <nextMove.icon size={22} />
            </span>
            <div className="min-w-0 flex-1">
              <p className="text-xs font-mono uppercase tracking-widest text-[#C2410C]">⚡ Your next best move</p>
              <p className="font-display text-xl text-stone-900 mt-0.5">{nextMove.title}</p>
              <p className="text-sm text-stone-600">{nextMove.sub}</p>
            </div>
            {nextMove.gain ? (
              <span className="shrink-0 text-right">
                <span className="font-display text-3xl vib-text">+{nextMove.gain}</span>
                <span className="block text-[10px] font-mono uppercase tracking-widest text-stone-500">pts potential</span>
              </span>
            ) : null}
            <ArrowRight size={18} className="shrink-0 text-[#C2410C] group-hover:translate-x-1 transition-transform" />
          </div>
        </Link>
      )}

      {/* Big score card */}
      {isNew && (
        <div className="mb-12 rounded-3xl bg-stone-900 text-stone-50 p-6 md:p-7 flex flex-wrap items-center justify-between gap-4" data-testid="dash-score-locked">
          <div><p className="overline" style={{ color: "rgba(253,251,247,0.55)" }}>Your Wishire Score</p><p className="font-display text-2xl mt-1">Unlocks after your first résumé score or mock interview.</p><p className="text-sm text-stone-400 mt-1">Recruiters shortlist by it. Three things build it: your résumé, a mock interview and a proctored test. One gives you a score up to 55, two up to 80, all three up to 100.</p></div>
          <div className="flex gap-2"><Link to="/app/resume" className="px-4 py-2.5 rounded-full bg-amber-300 text-stone-900 text-sm font-medium hover:bg-amber-200">Score my résumé</Link><Link to="/app/interview" className="px-4 py-2.5 rounded-full border border-white/20 text-sm font-medium hover:bg-white/10">Mock interview</Link></div>
        </div>
      )}
      <div className={`grid grid-cols-1 lg:grid-cols-12 gap-6 mb-12 items-start ${isNew ? "hidden" : ""}`}>
        <div className="lg:col-span-7 min-w-0 relative rounded-[2rem] vib-hero text-stone-50 p-10 md:p-12 overflow-hidden wsh-grain">
          <div className="relative grid grid-cols-1 md:grid-cols-2 gap-10 items-center">
            <div>
              <span className="inline-flex items-center gap-2 text-xs font-mono uppercase tracking-widest px-3 py-1 rounded-full"
                style={{ background: `${tier.color}26`, color: tier.color }}>
                <span className="w-1.5 h-1.5 rounded-full" style={{ background: tier.color }} /> {tier.name} tier
              </span>
              <h2 className="font-display text-6xl md:text-7xl mt-4 leading-none">
                <span data-testid="dash-composite-score"><CountUp value={loading ? 0 : score} /></span>
                <span className="text-stone-500 text-3xl"> / 100</span>
              </h2>
              <p className="mt-4 text-stone-300 max-w-sm leading-relaxed" data-testid="dash-readiness">
                {composite?.readiness ? <><b className="text-stone-50">{composite.readiness}.</b> {composite.readiness_note}</> : "Upload a résumé and run a mock interview to unlock your full score."}
              </p>

              {composite?.provisional && composite?.unlock?.length > 0 && (composite.has_resume || composite.has_interview || composite.has_mock_test) && (
                <p className="mt-3 text-xs text-amber-300/90 leading-relaxed" data-testid="dash-provisional">
                  Provisional: capped at {composite.evidence_cap} until you {composite.unlock.join(" and ")}. Each one lifts the cap.
                </p>
              )}
              {composite?.uncounted_attempts > 0 && (
                <p className="mt-2 text-xs text-stone-400" data-testid="dash-uncounted">
                  {composite.uncounted_attempts} short or blank attempt{composite.uncounted_attempts > 1 ? "s" : ""} not counted. Only full attempts move your score.
                </p>
              )}
              <div className="mt-6 space-y-3" data-testid="dash-breakdown">
                {composite?.breakdown &&
                  Object.entries(composite.breakdown).filter(([k, v]) => v.weight > 0 || v.score > 0 || k === "linkedin").map(([k, v]) => (
                    <Bar key={k} label={k} value={v.score} weight={v.weight} />
                  ))}
              </div>
            </div>
            <div className="flex justify-center">
              <div className="rounded-full vib-ring-glow">
                <ScoreRing value={score} size={280} dark />
              </div>
            </div>
          </div>
        </div>

        {/* Finish your profile — completion-aware */}
        <ProfileCompletion composite={composite} hasLinkedIn={hasLinkedIn} />
      </div>

      {!isNew && <ScoreHistoryChart history={history} />}

      {!isNew && <div className="mt-10"><StarterCoursesNudge /></div>}

      {/* Coaching plan widget — shown when user has at least one score */}
      {(hasResume || hasInterview) && (
        <div className="mt-10 mb-8">
          <div className="flex items-center justify-between mb-5">
            <div>
              <span className="overline">My Plan</span>
              <h2 className="font-display text-2xl mt-1">Your placement plan, one step at a time.</h2>
            </div>
            <Link
              to="/app/coach?tab=plan"
              className="text-sm text-[#C2410C] hover:text-stone-900 inline-flex items-center gap-1"
            >
              Full plan <ArrowRight size={14} />
            </Link>
          </div>
          <CoachingPlan autoLoad compact />
        </div>
      )}

      {/* LinkedIn score banner — shown when user has a linkedin score */}
      {linkedinScore != null && (
        <div className="mb-8 rounded-3xl border border-stone-200 bg-white p-6 md:p-8 flex items-center gap-6">
          <div className="p-4 rounded-2xl bg-blue-50 text-[#0A66C2] shrink-0">
            <Linkedin size={24} />
          </div>
          <div className="flex-1 min-w-0">
            <p className="overline mb-1">LinkedIn score</p>
            <div className="flex items-baseline gap-2">
              <span className="font-display text-4xl">{linkedinScore}</span>
              <span className="text-stone-400 text-xl">/ 100</span>
              <span className="ml-2 text-sm text-stone-500">
                {linkedinScore >= 70 ? "· Strong profile" : linkedinScore >= 45 ? "· Average profile" : "· Needs improvement"}
              </span>
            </div>
            <div className="mt-2 h-1.5 rounded-full bg-stone-100 overflow-hidden max-w-sm">
              <div
                className="h-full rounded-full transition-all duration-700"
                style={{
                  width: `${Math.min(100, linkedinScore)}%`,
                  background: linkedinScore >= 70 ? "#16a34a" : linkedinScore >= 45 ? "#d97706" : "#dc2626",
                }}
              />
            </div>
          </div>
          <Link
            to="/app/profile"
            className="shrink-0 inline-flex items-center gap-1 text-sm font-medium text-[#C2410C] hover:text-stone-900"
          >
            View profile <ArrowRight size={14} />
          </Link>
        </div>
      )}

      {/* Share my Wishire profile — once there is something worth sharing */}
      {score >= 50 && <ShareCard user={user} score={score} />}

      {/* Tip */}
      <div className="mt-6 rounded-3xl border border-stone-200 bg-white p-6 md:p-8 flex items-start gap-4">
        <div className="p-3 rounded-2xl bg-amber-100 text-[#C2410C]">
          <Sparkles size={18} />
        </div>
        <div>
          <p className="font-display text-xl">Tip · the mock interview moves the needle most</p>
          <p className="mt-2 text-stone-600 leading-relaxed">
            The interview is the heaviest part of your Wishire Score, and only full attempts count:
            stay at least 90 seconds and answer 3+ questions. Your last three attempts blend 50/30/20,
            so one bad day never defines you. Plan for one mock per week.
          </p>
        </div>
      </div>
    </div>
  );
}

function ShareCard({ user, score }) {
  const { refresh } = useAuth();
  const [enabled, setEnabled] = useState(!!user?.share_enabled);
  const [copied, setCopied] = useState(null);
  const [busy, setBusy] = useState(false);
  const handle = user?.public_handle;
  const origin = typeof window !== "undefined" ? window.location.origin : "";
  const publicUrl = handle ? `${origin}/u/${handle}` : "";
  const badgeUrl = handle ? `${origin}/api/public/badge/${handle}.svg` : "";
  const embed = handle
    ? `<a href="${publicUrl}" target="_blank" rel="noreferrer"><img src="${badgeUrl}" alt="My Wishire Score" width="320" height="80"/></a>`
    : "";

  const toggle = async () => {
    setBusy(true);
    try {
      await updateMe({ share_enabled: !enabled });
      setEnabled(!enabled);
      await refresh();
    } finally {
      setBusy(false);
    }
  };

  const copy = (k, v) => {
    navigator.clipboard.writeText(v);
    setCopied(k);
    setTimeout(() => setCopied(null), 1800);
  };

  return (
    <div className="mt-10 rounded-3xl bg-stone-900 text-stone-50 p-8 md:p-10 wsh-grain relative overflow-hidden" data-testid="share-card">
      <div
        className="absolute inset-0 opacity-40 pointer-events-none"
        style={{
          background:
            "radial-gradient(500px 320px at 80% 20%, rgba(217,119,6,0.35), transparent 60%)",
        }}
      />
      <div className="relative grid grid-cols-1 lg:grid-cols-12 gap-8 items-center">
        <div className="lg:col-span-7">
          <div className="inline-flex items-center gap-2 mb-3">
            <Share2 size={14} className="text-amber-300" />
            <span className="overline" style={{ color: "rgba(253,251,247,0.6)" }}>
              Share your Wishire Score
            </span>
          </div>
          <h3 className="font-display text-3xl md:text-4xl leading-tight">
            One public link.{" "}
            <span className="italic text-amber-300">Hire-me-ready in a tap.</span>
          </h3>
          <p className="mt-3 text-stone-300 max-w-xl leading-relaxed">
            Recruiters land on a clean Wishire profile with your composite score,
            verdict, and target role. Drop the badge in your LinkedIn About or
            email signature — it&apos;s a live SVG that updates as your score does.
          </p>

          <label className="mt-6 inline-flex items-center gap-3 cursor-pointer">
            <input
              type="checkbox"
              checked={enabled}
              onChange={toggle}
              disabled={busy}
              className="w-5 h-5 accent-amber-300"
              data-testid="share-toggle"
            />
            <span className="text-stone-100 text-sm">
              {enabled ? "Public sharing is ON" : "Turn on public sharing"}
            </span>
          </label>

          {enabled && (
            <div className="mt-6 space-y-4">
              <div>
                <p className="overline mb-2" style={{ color: "rgba(253,251,247,0.6)" }}>
                  Your public profile
                </p>
                <div className="flex items-center gap-2">
                  <input
                    readOnly
                    value={publicUrl}
                    className="flex-1 px-4 py-2.5 rounded-full bg-white/5 border border-white/15 text-stone-200 text-sm font-mono"
                    data-testid="share-url"
                  />
                  <button
                    onClick={() => copy("url", publicUrl)}
                    data-testid="share-copy-url"
                    className="inline-flex items-center gap-1 px-4 py-2.5 rounded-full bg-amber-300 text-stone-900 hover:bg-amber-200 text-sm font-medium"
                  >
                    <Copy size={14} /> {copied === "url" ? "Copied" : "Copy"}
                  </button>
                </div>
              </div>

              <div>
                <p className="overline mb-2" style={{ color: "rgba(253,251,247,0.6)" }}>
                  Embed badge (HTML)
                </p>
                <div className="flex items-stretch gap-2">
                  <pre className="flex-1 px-4 py-3 rounded-2xl bg-stone-950 border border-white/10 text-stone-300 text-xs font-mono overflow-x-auto wsh-no-scrollbar" data-testid="share-embed">
                    {embed}
                  </pre>
                  <button
                    onClick={() => copy("embed", embed)}
                    data-testid="share-copy-embed"
                    className="shrink-0 inline-flex items-center gap-1 px-4 rounded-2xl bg-amber-300 text-stone-900 hover:bg-amber-200 text-sm font-medium"
                  >
                    <Code size={14} /> {copied === "embed" ? "Copied" : "Copy"}
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>

        <div className="lg:col-span-5 flex justify-center">
          {enabled && handle ? (
            <a href={publicUrl} target="_blank" rel="noreferrer" className="inline-block">
              <img
                src={badgeUrl}
                alt="My Wishire Score badge"
                width={320}
                height={80}
                className="rounded-2xl shadow-[0_24px_60px_-20px_rgba(0,0,0,0.4)] hover:-translate-y-1 transition-transform"
                data-testid="share-badge-preview"
              />
            </a>
          ) : (
            <div className="w-[320px] h-[80px] rounded-2xl border-2 border-dashed border-white/20 flex items-center justify-center text-stone-400 text-xs font-mono uppercase tracking-widest">
              Badge preview — enable sharing
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

const BAR_LABEL = { resume: "Résumé", interview: "Mock interview", skills: "Skills match", communication: "Communication", mock_test: "Proctored test", linkedin: "LinkedIn" };

function Bar({ label, value, weight }) {
  return (
    <div>
      <div className="flex items-center justify-between gap-3 text-sm">
        <span className="text-stone-200 truncate min-w-0">{BAR_LABEL[label] || label}</span>
        <span className="font-mono text-xs text-stone-400 whitespace-nowrap shrink-0">
          {value}{weight > 0 ? ` · weight ${(weight * 100).toFixed(0)}%` : (label === "linkedin" ? " · shown, not weighted" : " · not yet")}
        </span>
      </div>
      <div className="mt-1 h-1.5 rounded-full bg-white/10 overflow-hidden">
        <div
          className="h-full transition-all duration-700"
          style={{ width: `${Math.min(100, value)}%`, background: "#FCD34D" }}
        />
      </div>
    </div>
  );
}

function ScoreHistoryChart({ history }) {
  if (!history || history.length < 2) return null;
  const max = 100;
  const w = 600, h = 120, pad = 12;
  const pts = history.slice(-20);
  const xStep = (w - pad * 2) / Math.max(pts.length - 1, 1);
  const yScale = (v) => h - pad - ((v / max) * (h - pad * 2));
  const points = pts.map((p, i) => `${pad + i * xStep},${yScale(p.score)}`).join(" ");
  const latest = pts[pts.length - 1]?.score ?? 0;
  const first = pts[0]?.score ?? 0;
  const delta = latest - first;

  return (
    <div className="mb-8 rounded-3xl border border-stone-200 bg-white p-6 md:p-8">
      <div className="flex items-center justify-between mb-4">
        <div>
          <span className="overline">Score history</span>
          <h3 className="font-display text-xl mt-1">Your Wishire Score over time</h3>
        </div>
        <span className={`text-sm font-mono font-bold ${delta >= 0 ? "text-emerald-600" : "text-red-500"}`}>
          {delta >= 0 ? "+" : ""}{delta} pts
        </span>
      </div>
      <svg viewBox={`0 0 ${w} ${h}`} className="w-full h-24" preserveAspectRatio="none">
        <defs>
          <linearGradient id="scoreGrad" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="#FCD34D" stopOpacity="0.3" />
            <stop offset="100%" stopColor="#FCD34D" stopOpacity="0" />
          </linearGradient>
        </defs>
        <polygon
          points={`${pad},${h - pad} ${points} ${pad + (pts.length - 1) * xStep},${h - pad}`}
          fill="url(#scoreGrad)"
        />
        <polyline points={points} fill="none" stroke="#F59E0B" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
        {pts.map((p, i) => (
          <circle key={i} cx={pad + i * xStep} cy={yScale(p.score)} r="3" fill="#F59E0B" />
        ))}
      </svg>
      <div className="flex justify-between text-xs text-stone-400 font-mono mt-1">
        <span>{pts[0]?.created_at ? new Date(pts[0].created_at).toLocaleDateString("en-IN", {day:"numeric",month:"short"}) : ""}</span>
        <span>{pts[pts.length-1]?.created_at ? new Date(pts[pts.length-1].created_at).toLocaleDateString("en-IN", {day:"numeric",month:"short"}) : "today"}</span>
      </div>
    </div>
  );
}

// Completion-aware "Finish your profile" panel.
function ProfileCompletion({ composite, hasLinkedIn }) {
  const bd = composite?.breakdown || {};
  const sig = (key) => Number(bd[key]?.score || 0) > 0;
  const SIGNALS = [
    { key: "resume", label: "Score your résumé", to: "/app/resume", icon: FileText, pts: 25, done: sig("resume") },
    { key: "interview", label: "Run a voice mock interview", to: "/app/interview", icon: Mic, pts: 40, done: sig("interview") },
    { key: "skills", label: "Map your skill gaps", to: "/app/coach?tab=plan#skills", icon: Brain, pts: 15, done: sig("skills") },
    { key: "linkedin", label: "Add your LinkedIn", to: "/app/linkedin", icon: Linkedin, pts: 10, done: hasLinkedIn || sig("linkedin") },
  ];
  const doneCount = SIGNALS.filter((s) => s.done).length;
  const pct = Math.round((doneCount / SIGNALS.length) * 100);
  const complete = doneCount === SIGNALS.length;

  return (
    <div className="lg:col-span-5 min-w-0 rounded-[2rem] bg-[#F5F3ED] p-8 md:p-10 border border-stone-200" data-testid="dash-profile-completion">
      <div className="flex items-center justify-between">
        <span className="overline">{complete ? "Profile complete" : "Finish your profile"}</span>
        <span className="text-sm font-mono text-stone-500">{doneCount}/{SIGNALS.length} done</span>
      </div>
      <h3 className="font-display text-2xl mt-2 mb-4">
        {complete ? "You're recruiter-ready. 🎉" : "Each step raises your score."}
      </h3>
      {/* progress bar */}
      <div className="h-2 rounded-full bg-stone-200 overflow-hidden mb-6">
        <div className="h-full rounded-full bg-emerald-500 transition-all duration-700" style={{ width: `${pct}%` }} />
      </div>

      <div className="space-y-2.5">
        {SIGNALS.map((s) => (
          <Link
            key={s.key}
            to={s.to}
            data-testid={`signal-${s.key}`}
            className={`group flex items-center gap-3 rounded-2xl border p-3.5 transition-all ${
              s.done
                ? "border-emerald-200 bg-emerald-50/60"
                : "border-stone-200 bg-white hover:border-stone-900 hover:shadow-[0_14px_30px_-20px_rgba(28,25,23,0.4)]"
            }`}
          >
            <span className={`inline-flex p-2 rounded-xl shrink-0 ${s.done ? "bg-emerald-100 text-emerald-700" : "bg-stone-100 text-stone-600"}`}>
              {s.done ? <CheckCircle2 size={16} /> : <s.icon size={16} />}
            </span>
            <div className="min-w-0 flex-1">
              <p className={`text-sm font-medium ${s.done ? "text-stone-500 line-through decoration-stone-300" : "text-stone-900"}`}>
                {s.label}
              </p>
            </div>
            {s.done ? (
              <span className="text-xs font-mono text-emerald-600 shrink-0">Done</span>
            ) : (
              <span className="shrink-0 inline-flex items-center gap-1 text-xs font-mono text-[#C2410C]">
                +{s.pts} <ArrowRight size={13} className="group-hover:translate-x-0.5 transition-transform" />
              </span>
            )}
          </Link>
        ))}
      </div>
    </div>
  );
}

// Tier name + vibrant color for the score.
function scoreTier(score) {
  if (score >= 90) return { name: "Platinum", color: "#60A5FA" };
  if (score >= 80) return { name: "Gold", color: "#F59E0B" };
  if (score >= 70) return { name: "Silver", color: "#94A3B8" };
  if (score >= 60) return { name: "Bronze", color: "#FB923C" };
  return { name: "Unranked", color: "#A8A29E" };
}

// Pick the single highest-impact action for the candidate right now.
function CampusStudentBanner() {
  const [s, setS] = useState(null);
  const [season, setSeason] = useState(null);
  const [rep, setRep] = useState(false);
  useEffect(() => { myStanding().then(({ data }) => setS(data)).catch(() => {}); mySeason().then(({ data }) => setSeason(data)).catch(() => {}); }, []);
  if (!s || !s.joined) return null;
  const seeReport = async () => {
    setRep(true);
    try { const { data } = await myReport(); if (!openCandidateReports(data, "What recruiters see")) alert("Allow pop-ups to open your report."); }
    catch (_) {} finally { setRep(false); }
  };

  return (
    <div className="mb-10 rounded-3xl bg-stone-900 text-stone-50 p-7 md:p-9 relative overflow-hidden wsh-grain" data-testid="dash-campus-banner">
      <div className="absolute inset-0 opacity-40 pointer-events-none" style={{ background: "radial-gradient(500px 320px at 88% 12%, rgba(217,119,6,0.45), transparent 60%)" }} />
      <div className="relative">
        <div className="flex items-center gap-2.5 text-amber-300">
          {s.college_logo ? (
            <img src={s.college_logo} alt={s.college_name} data-testid="dash-campus-logo" className="w-8 h-8 rounded-lg object-contain bg-white/90 border border-white/20" />
          ) : (
            <GraduationCap size={16} />
          )}
          <span className="text-xs font-mono uppercase tracking-widest">{s.college_name || "Your campus"}</span>
        </div>
        <h2 className="font-display text-2xl md:text-3xl mt-2">
          {s.batch_size > 1 && s.wishire_score > 0
            ? (s.percentile >= 50
              ? <>You&apos;re in the <span className="italic text-amber-300">top {s.top_pct}%</span> of your batch.</>
              : <>You&apos;re ahead of <span className="italic text-amber-300">{s.percentile}%</span> of your batch — climb with the plan below.</>)
            : s.eligible_drives > 0
            ? <>You have <span className="italic text-amber-300">{s.eligible_drives} drive{s.eligible_drives === 1 ? "" : "s"}</span> you can apply to.</>
            : <>Welcome to your <span className="italic text-amber-300">campus hub.</span></>}
        </h2>
        <div className="mt-6 grid grid-cols-2 sm:grid-cols-4 gap-3">
          <CampusStat icon={TrendingUp} label="Your score" value={s.wishire_score} />
          <CampusStat icon={Megaphone} label="Open drives" value={s.open_drives} />
          <CampusStat icon={CheckCircle2} label="Eligible for you" value={s.eligible_drives} highlight />
          <CampusStat icon={ArrowRight} label="Applied" value={s.applied} />
        </div>
        {season?.focus && (
          <p className="mt-5 text-sm text-stone-300 leading-relaxed max-w-xl" data-testid="dash-season-focus">
            <span className="text-amber-300 font-medium">This week:</span> {season.focus}{season.weakest && season.tier !== "D" ? ` Your weakest signal right now: ${season.weakest}.` : ""}
          </p>
        )}
        <div className="mt-6 flex flex-wrap items-center gap-3">
          <Link to="/app/campus" data-testid="dash-campus-cta"
            className="inline-flex items-center gap-2 px-5 py-3 rounded-full bg-amber-300 text-stone-900 hover:bg-amber-200 transition-colors font-medium">
            {s.eligible_drives > 0 ? `View ${s.eligible_drives} drive${s.eligible_drives === 1 ? "" : "s"} you can apply to` : "Go to campus drives"} <ArrowRight size={16} />
          </Link>
          <button onClick={seeReport} disabled={rep} data-testid="dash-my-report" className="inline-flex items-center gap-2 px-4 py-3 rounded-full border border-white/20 text-stone-200 hover:border-amber-300 hover:text-amber-300 text-sm transition-colors">
            {rep ? "Building…" : "See what recruiters see"}
          </button>
        </div>
      </div>
    </div>
  );
}
function CampusStat({ icon: Icon, label, value, highlight }) {
  return (
    <div className={`rounded-2xl p-4 border ${highlight ? "bg-amber-300/15 border-amber-300/30" : "bg-white/5 border-white/10"}`}>
      <Icon size={15} className={highlight ? "text-amber-300" : "text-stone-400"} />
      <p className="font-display text-3xl mt-2">{value}</p>
      <p className="text-[11px] font-mono uppercase tracking-widest text-stone-400 mt-0.5">{label}</p>
    </div>
  );
}

const EVIDENCE_CAP = { 0: 0, 1: 55, 2: 80, 3: 100 };
function computeNextMove(composite, user) {
  if (!composite) return null;
  const bd = composite.breakdown || {};
  const TARGET = 100; // max a signal can contribute = weight x 100; "up to +X points"
  const ACTIONS = {
    resume: { title: "Score your résumé", sub: "Résumé + skills match are a quarter of your score.", to: "/app/resume", icon: FileText },
    interview: { title: "Run a mock interview", sub: "The interview is the heaviest part of your score.", to: "/app/interview", icon: Mic },
    mock_test: { title: "Take a proctored test", sub: "The most trusted signal for recruiters.", to: "/app/interview?tab=test", icon: CheckCircle2 },
    skills: { title: "Close your skill gaps", sub: "Add the skills recruiters search for.", to: "/app/coach?tab=plan#skills", icon: Sparkles },
    linkedin: { title: "Audit your LinkedIn", sub: "Polish your profile for recruiter signal.", to: "/app/linkedin", icon: Linkedin },
  };

  // A missing core signal is always the biggest lift: it raises the cap itself.
  const missing = composite.missing || [];
  const have = 3 - missing.length;
  if (missing.length && have > 0) {
    const key = missing[0];
    const nextCap = EVIDENCE_CAP[have + 1];
    return { ...ACTIONS[key], sub: `Lifts your score cap from ${EVIDENCE_CAP[have]} to ${nextCap}.`, gain: Math.max(1, nextCap - (composite.score || 0)) };
  }

  let best = null;
  for (const key of Object.keys(ACTIONS)) {
    const comp = bd[key];
    if (!comp) continue;
    const cur = comp.score ?? 0;
    const weight = comp.weight ?? 0;
    const gain = Math.round(weight * Math.max(0, TARGET - cur)); // composite points recoverable
    if (gain > 0 && (!best || gain > best.gain)) {
      best = { ...ACTIONS[key], gain };
    }
  }
  if (best) return best;

  // Everything maxed — nudge identity verification for the permanent +3 bonus
  const verified = user?.identity_verified || user?.verification_level;
  if (!verified) {
    return { title: "Verify your identity", sub: "Earn a permanent +3 bonus that never decays.", to: "/app/score-insights", icon: CheckCircle2, gain: 3 };
  }
  return null;
}
